<template>
    <div v-dark class="study-activity">
        <div v-dark class="study-activity__head">
            <div v-dark class="study-activity__title">
                <Icon type="activity" />
                <h2>Study Activity</h2>
            </div>
            <FilterDropdown
                v-model="filterOptions"
                class="study-activity__filter"
                :align="breakpoint === 'black-bear' ? 'right' : 'left'"
                size="small"
                :responsive-button="true"
                filterLabel="Filter"
                filterOptionsLabel="Filter by Quiz Mode"
                :radioEntries="[]"
                :checkboxEntries="filterOptions"
                :is-dark-mode="isDarkMode"
            />
        </div>
        <div class="study-activity__stats">
            <div
                v-for="stat in summaryStats"
                :key="stat.label"
                v-dark
                class="study-activity__stat"
                :class="{ 'study-activity__stat--wide': stat.isWide }"
                :aria-label="`${stat.value} ${stat.label}`"
            >
                <div v-dark
                     class="study-activity__stat-number"
                     aria-hidden="true">
                    {{ stat.value }}
                </div>
                <div v-dark
                     class="study-activity__stat-label"
                     aria-hidden="true">
                    {{ stat.label }}
                </div>
            </div>
        </div>
        <div v-if="enabledFilters.length" class="study-activity__filters">
            <div v-dark class="study-activity__filters-label">
                Filters:
            </div>
            <Pill
                v-for="filter in enabledFilters"
                :key="filter.id"
                :show-close="true"
                :is-dark-mode="isDarkMode"
                @click="removeFilter(filter.id)"
            >
                {{ filter.label }}
            </Pill>
        </div>
        <div v-if="enabledFilters.length && !studyActivities.length" class="study-activity__no-activity">
            <div v-dark class="study-activity__no-activity-label">
                There isn’t any study activity that<br>matches your filter criteria.
            </div>
        </div>
        <div v-else-if="!studyActivities.length" class="study-activity__no-activity">
            <div v-dark class="study-activity__no-activity-label">
                You haven't taken any quizzes
            </div>
            <PocketButton
                :is-dark-mode="isDarkMode"
                :disabled="isLoadingQuick10"
                @click="startQuick10"
            >
                Take a Quick 10
            </PocketButton>
        </div>
        <div v-else class="study-activity__activity-list">
            <div
                v-for="activity in studyActivities.slice(currentPage * quizzesPerPage, pageLastIndex)"
                :key="activity.quizId"
                v-dark
                class="study-activity__activity"
                tabindex="0"
                :aria-label="`${activity.name} ${activity.date} ${activity.score} ${activity.isLocked ? 'locked' : ''}`"
                @click="activityClicked(activity)"
                @keydown.enter="activityClicked(activity)"
                @mousedown.prevent
            >
                <StudyActivityCard
                    class="study-activity__activity-card"
                    :activity="activity"
                    :is-loading="isLoading"
                    :is-dark-mode="isDarkMode"
                    :current-exam="currentExam"
                />
            </div>
        </div>
        <div
            v-if="studyActivities.length"
            class="study-activity__pagination"
        >
            <TablePagination
                class="study-activity__pagination-arrows"
                :perPage="quizzesPerPage"
                unit="quizzes"
                :currentPage="currentPage"
                :total="studyActivities.length"
                :is-dark-mode="isDarkMode"
                @changePage="changePage"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { quizModule } from '@/store/quiz/module'
import { userModule } from '@/store/user/module'
import { formatStudyTime, getScoreRange, months } from '@/utils'
import UIKit, { utils } from '@pocketprep/ui-kit'
import { qotdModule } from '@/store/qotd/module'
import { subscriptionModule } from '@/store/subscription/module'
import { mockExamModule } from '@/store/mockExam/module'
import { screenModule } from '@/store/screen/module'
import { bundleModule } from '@/store/bundle/module'
import { examMetadataModule } from '@/store/examMetadata/module'
import { assignmentModule } from '@/store/assignment/module'
import type { TFilterOption } from '@/utils'
import StudyActivityCard from './StudyActivityCard.vue'
import type { Study } from '@pocketprep/types'

const {
    Icon,
    Button: PocketButton,
    Pill,
    FilterDropdown,
    TablePagination,
} = UIKit

type TStudyActivity = {
    quizId: string
    isLocked: boolean
    icon: string
    name: string
    nameSuffix: string
    iconColor: string
    date: string
    score: string
    scoreRange: string
}

const router = useRouter()

const isLoading = ref(true)
const isLoadingQuick10 = ref(false)
const currentPage = ref(0)
const filterOptions = ref<TFilterOption[]>([
    {
        id: 'qotd',
        label: 'Question of the Day',
        enabled: false,
    },
    {
        id: 'quick10',
        label: 'Quick 10',
        enabled: false,
    },
    {
        id: 'timed',
        label: 'Timed Quiz',
        enabled: false,
    },
    {
        id: 'missed',
        label: 'Missed Questions',
        enabled: false,
    },
    {
        id: 'weakest',
        label: 'Weakest Subject',
        enabled: false,
    },
    {
        id: 'concept',
        label: 'Concept Quiz',
        enabled: false,
    },
    {
        id: 'custom',
        label: 'Build Your Own',
        enabled: false,
    },
])

const isDarkMode = computed(() => {
    return userModule.state.settings.isDarkMode
})

const breakpoint = computed(() => {
    return screenModule.getters.getBreakpoint()
})

const quizzesPerPage = computed(() => {
    return breakpoint.value === 'black-bear' || breakpoint.value === 'brown-bear' ? 8 : 10
})

const summaryStats = computed(() => {
    const quizzes = quizModule.state.quizzes
    const quizCount = quizzes.filter((q) => q.mode !== -1 && q.mode !== 5).length
    const qotdCount = quizzes.filter((q) => q.mode === -1).length

    const studySeconds = quizzes.reduce((acc, q) => {
        return q.mode === 5 ? acc : acc + q.durationSeconds
    }, 0)

    return [
        { label: 'Quizzes', value: String(quizCount), isWide: false },
        { label: 'QotD', value: String(qotdCount), isWide: false },
        { label: 'Study Time', value: formatStudyTime(studySeconds), isWide: true },
    ]
})

const pageLastIndex = computed(() => {
    const lastIndex = (currentPage.value * quizzesPerPage.value) + quizzesPerPage.value

    return lastIndex > studyActivities.value.length
        ? studyActivities.value.length
        : lastIndex
})

watch(quizzesPerPage, () => {
    currentPage.value = 0
})

const enabledFilters = computed(() => {
    return filterOptions.value.reduce((acc, option) => {
        if (!option.enabled) {
            return acc
        }

        acc.push(option)
        return acc
    }, [] as TFilterOption[])
})


const todaysQotD = computed(() => {
    return qotdModule.getters.getQotDQuestion()
})

const areMockExamsAvailable = computed(() => {
    return mockExamModule.getters.getCurrentMockExams().length > 0
})

const areAssignmentsEnabled = computed(() => {
    // assignments are enabled when an org has created at least one assignment
    return assignmentModule.getters.getAreAssignmentsEnabled()
})

const sortedQuizzes = computed(() => {
    return [ ...quizModule.getters.getQuizzes() ]
        .sort((a, b) => {
            return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        })
})

const studyActivities = computed(() => {
    const enabledFilterIds = enabledFilters.value.map((f) => f.id)

    return sortedQuizzes.value.reduce<TStudyActivity[]>((acc, q) => {
        if (enabledFilters.value.length) {
            if (
                (!enabledFilterIds.includes('qotd') && q.mode === -1)
                || (!enabledFilterIds.includes('quick10') && q.mode === 0)
                || (!enabledFilterIds.includes('missed') && q.mode === 2)
                || (!enabledFilterIds.includes('weakest') && q.mode === 3)
                || (!enabledFilterIds.includes('timed') && q.mode === 4)
                || (!enabledFilterIds.includes('mockExam') && q.mode === 5)
                || ((!enabledFilterIds.includes('levelUp') && (q.mode === 6 || q.mode === 7)))
                || (!enabledFilterIds.includes('custom') && q.mode === 10)
                || (!enabledFilterIds.includes('assignment') && q.mode === 11)
                || (!enabledFilterIds.includes('concept') && q.mode === 12)) {
                return acc
            }
        }

        // if study mode isn't in library AND isn't 1 (aka legacy), remove from quizzes
        if (q.mode !== 1 && !utils.studyModes[q.mode as -1 | 0 | 2 | 3 | 4 | 10 | 11 | 12 | 5 | 6 | 7]) {
            return acc
        }

        const { icon, name, iconColor, iconColorDM } =
            utils.studyModes[q.mode === 1 ? 0 : q.mode as -1 | 0 | 2 | 3 | 4 | 10 | 11 | 12 | 5 | 6 | 7]
        const date = new Date(q.createdAt)
        const score = Math.round((q.correctCount / (q.incorrectCount + q.correctCount)) * 100)
        const isLocked = !hasActiveSubscription.value && name === 'Question of the Day'
            && todaysQotD.value?.serial !== q.answers[0]?.questionSerial
        const prebuiltQuiz = q.prebuiltQuiz as Study.Class.PrebuiltQuizJSON
        const nameSuffix = prebuiltQuiz?.name
            ? ` ${prebuiltQuiz.name}`
            : ''

        acc.push({
            quizId: q.objectId,
            icon,
            name: name,
            nameSuffix: nameSuffix,
            isLocked,
            iconColor: isDarkMode.value ? iconColorDM : iconColor,
            date: `${months[date.getMonth()]?.slice(0, 3)} ${date.getDate()}`,
            score: q.mode === -1
                ? q.correctCount === 1
                    ? 'correct'
                    : 'incorrect'
                : `${score}%`,
            scoreRange: getScoreRange(score),
        })

        return acc
    }, [])
})

const hasActiveSubscription = computed(() => {
    return !!subscriptionModule.getters.getSubscriptionForExamId()
})

const currentExam = computed(() => {
    return examMetadataModule.getters.getCurrentExamMetadata()
})

onMounted(async () => {
    await Promise.all([
        userModule.actions.fetchUserData(),
        qotdModule.actions.fetchCurrentQotDQuestion(),
        mockExamModule.actions.fetchMockExams(),
        bundleModule.actions.fetchBundles(),
        examMetadataModule.actions.fetchExamMetadata(),
    ])

    const levelUpIsAvailable = await isLevelUpEnabled()
    if (levelUpIsAvailable && !filterInOpts('levelUp')) {
        filterOptions.value.push({
            id: 'levelUp',
            label: 'Level Up',
            enabled: false,
        })
    }

    if (areMockExamsAvailable.value && !filterInOpts('mockExam')) {
        filterOptions.value.push({
            id: 'mockExam',
            label: 'Mock Exam',
            enabled: false,
        })
    }

    if (areAssignmentsEnabled.value && !filterInOpts('assignment')) {
        filterOptions.value.push({
            id: 'assignment',
            label: 'Assignment',
            enabled: false,
        })
    }

    isLoading.value = false
})

const removeFilter = (filterId: string) => {
    filterOptions.value = filterOptions.value.map((o) => {
        return o.id === filterId ? { ...o, enabled: false } : o
    })
}

const filterInOpts = (filterId: string) => {
    return filterOptions.value.some((opt) => {
        return opt.id === filterId
    })
}

const isLevelUpEnabled = async () => {
    if (currentExam.value?.examGuid) {
        return await quizModule.actions.isLevelUpEnabled({
            examGuid: currentExam.value.examGuid,
        })
    }

    return false
}

const activityClicked = (activity: TStudyActivity) => {
    if (activity.name !== 'Question of the Day') {
        router.push({
            name: 'quiz-result',
            params: {
                quizId: activity.quizId,
            },
        })
    } else {
        router.push({
            name: 'qotd',
            params: {
                quizId: activity.quizId,
            },
        })
    }
}

const startQuick10 = async () => {
    if (!isLoadingQuick10.value) {
        isLoadingQuick10.value = true
        await quizModule.actions.createActiveQuiz({ mode: 'quick10', questionCount: 10 })

        router.push({ name: 'quiz' })
        isLoadingQuick10.value = false
    }
}

const changePage = (val: number) => {
    currentPage.value = val
}

const updateFilterOptions = async () => {
    const levelUpIsAvailable = await isLevelUpEnabled()
    if (areMockExamsAvailable.value && !filterInOpts('mockExam')) {
        filterOptions.value.push({
            id: 'mockExam',
            label: 'Mock Exam',
            enabled: false,
        })
    }
    if (!areMockExamsAvailable.value) {
        filterOptions.value = filterOptions.value.filter((opt) => {
            return opt.id !== 'mockExam'
        })
    }

    if (levelUpIsAvailable && !filterInOpts('levelUp')) {
        filterOptions.value.push({
            id: 'levelUp',
            label: 'Level Up',
            enabled: false,
        })
    }
    if (!levelUpIsAvailable) {
        filterOptions.value = filterOptions.value.filter((opt) => {
            return opt.id !== 'levelUp'
        })
    }

    if (areAssignmentsEnabled.value && !filterInOpts('assignment')) {
        filterOptions.value.push({
            id: 'assignment',
            label: 'Assignment',
            enabled: false,
        })
    }
    if (!areAssignmentsEnabled.value) {
        filterOptions.value = filterOptions.value.filter((opt) => {
            return opt.id !== 'assignment'
        })
    }
}

// there is not a way to look to see if an exam has changed
// but we can see if there is a change in sorted quizzes
watch(sortedQuizzes, async () => {
    await updateFilterOptions()
})

watch(filterOptions, () => {
    currentPage.value = 0
}, { deep: true })
</script>

<style lang="scss" scoped>
.study-activity {
    background: $white;
    border: 1px solid $gray-divider;
    border-radius: 6px;
    padding: 11px 14px 12px;
    box-sizing: border-box;
    min-height: 428px;
    display: flex;
    position: relative;
    flex-direction: column;

    &--dark {
        color: $fog;
        background: $brand-black;
        border-color: $slate;
    }

    &__head {
        display: flex;
        justify-content: space-between;
        align-items: center;
        box-sizing: border-box;
        height: 32px;
        padding-bottom: 7px;
        border-bottom: 2px solid $gray-divider;
        padding-left: 8px;

        &--dark {
            padding-bottom: 8px;
            border-bottom-width: 2px;
            border-color: $ash;
        }

        h2 {
            margin: 0;
            font-weight: 600;
            font-size: 16px;
            line-height: 22px;
        }
    }

    &__title {
        font-size: 16px;
        line-height: 22px;
        font-weight: 600;
        display: flex;
        align-items: center;

        svg {
            height: 26px;
            width: 21px;
            margin-right: 8px;
            color: $slate;
        }

        &--dark {
            color: $white;
        }

        &--dark svg {
            color: $fog;
        }
    }

    &__filter {
        margin-right: 0;
    }

    &__stats {
        display: flex;
        gap: 14px;
        margin: 12px 0;
    }

    &__stat {
        flex: 1;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        background-color: $pearl;
        border-radius: 6px;
        height: 69px;
        gap: 2px;

        &--wide {
            flex: 1.6;
        }

        &--dark {
            background-color: $charcoal;

            @include breakpoint(black-bear) {
                background-color: $black-feather;
            }
        }
    }

    &__stat-number {
        font-weight: 600;
        font-size: 26px;
        letter-spacing: 0.3px;
        line-height: 36px;
        height: 32px;
    }

    &__stat-label {
        font-size: 13px;
        line-height: 18px;
        color: $ash;

        &--dark {
            color: $fog;
        }
    }

    &__no-activity {
        margin-top: 55px;
        text-align: center;
    }

    &__no-activity-label {
        font-size: 14px;
        line-height: 19px;
        color: $slate-03;
        margin-bottom: 24px;

        &--dark {
            color: $white;
        }
    }

    &__activity-list {
        flex: 1;
        display: flex;
        flex-direction: column;
    }

    &__pagination {
        display: flex;
        justify-content: flex-end;
        height: 36px;
        align-items: flex-end;
        outline: none;
    }

    &__pagination-arrows {
        display: flex;
        margin: 0 4px;

        :deep(.uikit-table-pagination__previous) {
            margin-right: 6px;
        }
    }

    &__filters {
        margin: 10px 0 6px;
    }

    &__filters-label {
        font-size: 13px;
        line-height: 22px;
        color: $slate-03;
        margin-right: 9px;
        display: inline-block;
        margin-left: 12px;
        vertical-align: top;

        @include breakpoint(black-bear) {
            margin-left: 0;
        }

        &--dark {
            color: $white;
        }
    }
}
</style>
